import { BrowserRouter, MemoryRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import React, { useEffect, useRef, useState } from 'react'

import { Insights } from './components/insights/Insights'
import { Login } from './components/login'
import { useApplicationContext } from './contexts/appContext'
import warningIcon from './img/info-icon-warning.png'

import { Success } from './components/success/Success'
import { Cancel } from './components/cancel/Cancel'
import { Error } from './components/error/Error'
import { UnAuthorised } from './components/unAuthorised/UnAuthorised'
import { LoadingDialog, Code, Layout, Header } from '@verifier-user/verifier-cdr'
import { Welcome } from './components/login/Welcome'
import useMatomo from '@verifier-user/verifier-matomo'

const AppRoutes = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { vendorSettings, appType, accessCodeError, requestId } = useApplicationContext()
  const [trackersLoaded, setTrackersLoaded] = useState(false)

  const { initMatomo, trackPageView, addTrackEvent } = useMatomo()

  //taken from https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  const defaultOnErrorFn = useRef(window.onerror)
  //to fix the ResizeObserver loop limit exceeded error
  useEffect(() => {
    window.onerror = (...args) => {
      if (args[0] === 'ResizeObserver loop limit exceeded') {
        return true
      } else {
        defaultOnErrorFn.current && defaultOnErrorFn.current(...args)
      }
    }
    return () => {
      window.onerror = defaultOnErrorFn.current
    }
  }, [])

  useEffect(() => {
    if (!trackersLoaded && requestId) {
      initMatomo(vendorSettings?.viewSettings?.trackerConfig, requestId)
      setTrackersLoaded(true)
    }
  }, [trackersLoaded, requestId])

  useEffect(() => {
    trackPageView()
  }, [location.pathname])

  useEffect(() => {
    const handleQueueProcessed = () => {
      if (sessionStorage.getItem(process.env.APP) && location.pathname === '/') {
        navigate('/init-widget')
      }
    }

    window.addEventListener('queueProcessed', handleQueueProcessed)

    return () => {
      window.removeEventListener('queueProcessed', handleQueueProcessed)
    }
  }, [location.pathname])

  const InvalidAccessCodeRenderer = () => {
    const searchParams = new URLSearchParams(window.location.search)
    const accessCode = searchParams.get('bmid') || searchParams.get('accessCode')
    const { vendorSettings, logout } = useApplicationContext()

    useEffect(() => {
      addTrackEvent('ACCESS_CODE_ERROR', 'invalid_access_code', accessCode)
    }, [])

    return (
      <>
        <Header vendorSettings={vendorSettings} />
        <Layout
          vendorSettings={vendorSettings}
          isAuthenticated={() => {
            return false
          }}
          logout={logout}
        >
          <div className={'warning-container'}>
            <img src={warningIcon} alt={'warning-logo'} />
            <p className='text-lg text-black item2' data-testid='welcome-brand-text'>
              <strong>That seems to be an Invalid link.</strong>
            </p>
          </div>
          <p className='text-lg' data-testid='welcome-brand-text'>
            Please double check the web link provided and make sure the whole link is copied into your browser.
          </p>
        </Layout>
      </>
    )
  }

  const CodeWithContext = () => {
    const { customerLogin, setCustomerLogin, logout, setToken, isAuthenticated, apiUrl, testUserId, accessCode } =
      useApplicationContext()

    const loginUrl = testUserId
      ? `${apiUrl}/${accessCode}/code?testUserId=${testUserId}`
      : `${apiUrl}/${accessCode}/code`

    const verifyCodeUrl = testUserId
      ? `${apiUrl}/${accessCode}/verify/[code]?testUserId=${testUserId}`
      : `${apiUrl}/${accessCode}/verify/[code]`

    return (
      <Code
        vendorSettings={vendorSettings}
        customerLogin={customerLogin}
        setCustomerLogin={setCustomerLogin}
        logout={logout}
        path={'/init-widget'}
        setToken={setToken}
        isAuthenticated={isAuthenticated}
        loginUrl={loginUrl}
        verifyCodeUrl={verifyCodeUrl}
      />
    )
  }

  return (
    <div id='page-container' className='container-type-inline-size'>
      <div id='content-wrap'>
        {accessCodeError && trackersLoaded && <InvalidAccessCodeRenderer />}
        <LoadingDialog id={'fnf'} isOpen={!appType && !accessCodeError} />
        {appType && !accessCodeError && trackersLoaded && (
          <Routes>
            <Route path='/' element={<Login vendorSettings={vendorSettings} />} />
            <Route path='/welcome' element={<Welcome vendorSettings={vendorSettings} />} />
            <Route path='/code' element={<CodeWithContext />} />
            <Route path='/init-widget' element={<Insights />} />
            <Route path={'/success'} element={<Success />} />
            <Route path={'/cancel'} element={<Cancel />} />
            <Route path={'/error'} element={<Error />} />
            <Route path={'/unAuthorised'} element={<UnAuthorised />} />
          </Routes>
        )}
      </div>
    </div>
  )
}

const Router = () => {
  if (!process.env.MEMORY_ROUTER) {
    return (
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    )
  } else {
    return (
      <MemoryRouter>
        <AppRoutes />
      </MemoryRouter>
    )
  }
}

const App = () => {
  return <Router />
}

export default App
